import OrganisationTypeEnum from '@constants/enums/organisation/type';
import UserRoleEnum from '@constants/enums/user/role';
import UserStatusEnum from '@constants/enums/user/status';

import i18n from '@plugins/i18n';

import { Collection, User } from '@/models';

export class SettingsUsersCollection extends Collection {
    static type = 'SettingsUsersCollection';
    static endpoint = User.api.index;
    static model = User;

    static fields() {
        return {
            ...super.fields(),
            organisation_id: this.string(null).nullable(),
            organisation_type: this.string(null).nullable(),
            show_visibility_statistics: this.boolean(false),
            external: this.boolean(false)
        };
    }

    get preset_filters() {
        let filters = {};

        filters.include_statistics = true;

        if (this.external && this.organisation_id) {
            filters.role_in_organisation = this.organisation_id;
            filters.organisation_id = this.filterOperator('is_not') + ':' + this.organisation_id;
        } else if (this.organisation_id) {
            filters.organisation_id = this.filterOperator('is') + ':' + this.organisation_id;
        }

        return filters;
    }

    get headers() {
        return [
            this.createHeader('name', i18n.t('name'), {
                width: '18rem',
                minWidth: '18rem',
                filterable: true,
                sortable: true,
                mock: faker => faker.person.fullName()
            }),
            this.createHeader('email', i18n.t('email'), {
                active: false,
                width: '15rem',
                filterable: true
            }),
            this.createHeader('can_login', i18n.t('login_enabled'), {
                format: 'boolean',
                active: false,
                filterable: true,
                mock: faker => faker.datatype.boolean()
            }),
            this.createHeader('role', i18n.t('role'), {
                format: 'enum',
                enum: UserRoleEnum,
                enumA11yKey: 'enums.user_role',
                multiple: true,
                width: '11rem',
                sortable: false,
                filterable: true,
                transform: data => {
                    let roles = [];

                    if (data.item.roles) {
                        data.item.roles.forEach(role => {
                            if (this.organisation_id && role.organisation_id !== this.organisation_id) {
                                return;
                            }

                            roles.push(...role.roles);
                        });
                    }

                    return [...new Set([...roles])];
                },
                mock: faker =>
                    faker.helpers
                        .arrayElements(Object.values(UserRoleEnum), { min: 1, max: 3 })
                        .map(role => ({ roles: [role] }))
            }),
            this.createHeader('organisation_name', i18n.t('organisation'), {
                path: 'organisation.name',
                width: '11rem',
                filterable: true,
                active: this.organisation_type !== 'INVESTOR' && this.external
            }),
            this.createHeader('organisation_type', i18n.t('organisation_type'), {
                active: false,
                filterable: true,
                transform: data => data?.item?.organisation?.type,
                width: '11rem',
                format: 'enum',
                enum: OrganisationTypeEnum,
                enumA11yKey: 'enums.organisation_type'
            }),
            this.createHeader('fca_number', i18n.t('fca_number'), {
                active: false,
                filterable: true,
                width: '11rem'
            }),
            this.createHeader('primary_contact', i18n.t('primary_contact'), {
                format: 'boolean',
                sortable: false,
                filterable: true,
                width: '10rem',
                active: this.organisation_type === 'ADVISER'
            }),

            this.createHeader('status', i18n.t('status'), {
                filterable: true,
                align: 'center',
                width: '8rem',
                format: 'enum',
                enum: UserStatusEnum,
                enumA11yKey: 'enums.user_status',
                enumTypes: {
                    [UserStatusEnum.ACTIVE]: 'success',
                    [UserStatusEnum.INACTIVE]: 'grey',
                    [UserStatusEnum.SUSPENDED]: 'error',
                    [UserStatusEnum.UNVERIFIED]: 'warning'
                }
            }),
            this.createHeader('last_logged_in_at', i18n.t('last_logged_in_at'), {
                format: 'datetime',
                width: '12rem',
                align: 'right',
                filterable: {
                    presetTime: '00:00:00',
                    outputFormat: 'YYYY-MM-DD'
                },
                sortable: true,
                active: true
            }),
            this.createHeader('visibility_groups_contact_count', i18n.t('primary_contact'), {
                format: 'number',
                width: '11rem',
                align: 'right',
                filterable: false,
                sortable: true,
                active: this.organisation_type === 'ADVISER' && this.show_visibility_statistics
            }),
            this.createHeader('visibility_groups_grantee_count', i18n.t('viewer'), {
                format: 'number',
                width: '8rem',
                align: 'right',
                filterable: false,
                sortable: true,
                active: this.organisation_type === 'ADVISER' && this.show_visibility_statistics
            }),
            this.createHeader('visibility_groups_investor_count', i18n.t('visibility'), {
                format: 'number',
                width: '8rem',
                align: 'right',
                filterable: false,
                sortable: true,
                active: this.organisation_type === 'ADVISER' && this.show_visibility_statistics
            }),
            this.createHeader('control', '', {
                format: 'control',
                width: '14rem',
                align: 'center'
            })
        ];
    }

    get default_headers() {
        if (this.external) {
            return [
                'name',
                'role',
                'organisation_name',
                'primary_contact',
                'status',
                'last_logged_in_at',
                'visibility_groups_contact_count',
                'visibility_groups_grantee_count',
                'visibility_groups_investor_count',
                'control'
            ];
        }
        return [
            'name',
            'role',
            'primary_contact',
            'status',
            'last_logged_in_at',
            'visibility_groups_contact_count',
            'visibility_groups_grantee_count',
            'visibility_groups_investor_count',
            'control'
        ];
    }

    get header_groups() {
        if (!(this.organisation_type === 'ADVISER' && this.show_visibility_statistics)) {
            return [];
        }

        return [
            {
                text: i18n.t('visibility_group_metrics'),
                columns: [
                    'visibility_groups_contact_count',
                    'visibility_groups_grantee_count',
                    'visibility_groups_investor_count'
                ]
            }
        ];
    }

    get export_headers() {
        return [
            'name',
            'email',
            'can_login',
            'role',
            'organisation_name',
            'organisation_type',
            'fca_number',
            'primary_contact',
            'status',
            'last_logged_in_at',
            'visibility_groups_contact_count',
            'visibility_groups_grantee_count',
            'visibility_groups_investor_count'
        ];
    }
}

export default SettingsUsersCollection;
